import {
  CloseButton,
  ListContext,
  ListContextValue,
  Page,
  PageRoute,
  Stack,
  Typography,
  buildPath,
  buildTemplatePath,
  newToast,
  pn,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { SubmitButton } from "components";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  StudentBulkUpdate,
  StudentBulkUpdateResource,
  StudentBulkUpdateSchema,
  StudentServiceBulkUpdate
} from "generated/openApi";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";
import { SelectedStudents } from "./SelectedStudents";
import { StudentUpdateSchema } from "./StudentUpdateSchema";

const routeName = "studentBulkUpdate";

export const studentBulkUpdatePageRoute: PageRoute = {
  handle: "Batch Update Students",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type StudentBulkUpdatePageState = {
  selectedIds: number[];
};

export const StudentBulkUpdatePage = () => {
  const dispatch = useDispatch();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<StudentBulkUpdatePageState>(state);
  const [bulkUpdate, setBulkUpdate] = useState<StudentBulkUpdate>({});
  const [resource, setResource] = useState<StudentBulkUpdateResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [bulkUpdate] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  useEffect(() => {
    getBulkUpdate(stateParam.selectedIds);

    ApiService.students.getBulkUpdateResource().then((response) => {
      setResource(response);
    });
  }, []);

  const getBulkUpdate = (ids: number[]) => {
    ApiService.students.getBulkUpdate(ids).then((response) => {
      setBulkUpdate(response);
      resetUnsavedChanges([response]);
    });
  };

  const save = (bulkUpdate: StudentBulkUpdate) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    ApiService.students
      .saveBulkUpdate(bulkUpdate)
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          dispatch(setAppToast(newToast("error")));
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
        } else {
          dispatch(setAppToast(newToast("success")));
          resetUnsavedChanges([bulkUpdate]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.response);
      });
  };

  return (
    <Page
      isForm
      header={
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="inherit">{studentBulkUpdatePageRoute.handle}</Typography>

          <Stack direction="row" alignItems="center" spacing="sm">
            <CloseButton confirm={unsavedChanges} />

            <SubmitButton
              onClick={() => save(bulkUpdate)}
              disabled={!(bulkUpdate.students && bulkUpdate.students?.length > 0 && unsavedChanges)}
            />
          </Stack>
        </Stack>
      }
    >
      <Stack spacing="lg">
        <StudentUpdateSchema
          updateSchema={bulkUpdate.updateSchema || ({} as StudentBulkUpdateSchema)}
          setUpdateSchema={(value) => setBulkUpdate({ ...bulkUpdate, updateSchema: value })}
          resource={resource}
          validationErrors={validationErrors?.[pn<StudentServiceBulkUpdate>("updateSchema")] || []}
        />

        <SelectedStudents
          students={new Map(bulkUpdate.students?.map((x) => [x.id, x]))}
          setStudents={(value) => setBulkUpdate({ ...bulkUpdate, students: Array.from(value.values()) })}
          errors={selectedItemsErrors}
          setErrors={setSelectedItemsErrors}
        />
      </Stack>
    </Page>
  );
};
